/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Divider,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  OverflowTooltip,
  Text,
  TextLabel,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { SearchOIcon } from '@tonic-one/react-icons';
import { useToggle } from '@tonic-one/react-hooks';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { QuarantineApi, ProvisionApi } from '@Apis';
import { useOrgStore } from '@Models/useOrgModel';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import { MenuOption, ServiceNameEnum } from '@Types/index';
import {
  Criterion,
  QuarantineLogTypeEnum,
  SearchItem,
  TimeRangeInterface,
} from '@Types/log';
import {
  getSortedServiceIds,
  SERVICE_ID_NAME_MAP,
  SERVICE_NAME_ID_MAP,
} from '@Utils/service';
import { getUtcTime } from '@Utils/time';
import { logArrayFields, mappingFields, timeFields } from '@Log/constants';
import { LogFacet as QuarantineFacet } from '@Log/LogFacet';
import SearchCriteria from '@Log/SearchCriteria';
import {
  LogTimeUnit,
  getTimeRange,
  removeUrlState,
  wrapLogFieldFuncMap,
} from '@Log/shared';
import TimeRange from '@Log/TimeRange';
import {
  QuarantineLogContextProvider,
  useQuarantineReducerContext,
} from '@Quarantine/Quarantine/context';
import QuarantineTable from '@Quarantine/QuarantineTable';
import {
  QUARANTINE_TYPE_NAME_MAP,
  quarantineConfig,
  quarantineUnsearchableFields,
} from '@Quarantine/utils/constants';
import {
  QuarantineLogContentReqDto,
  QuarantineLogFacetReqDto,
  QuarantineLogItem,
  QuarantineLogState,
} from '@Quarantine/utils/types';
import CSpinner from '../../../components/CSpinner';
import { isInUIC } from '../../../utils/common';
import { convertScanSourceToQuarantineType } from '../utils/fns';
import { MappingField } from '../../Log/type';
import { riskAiName } from '@Quarantine/RiskAI/utils';
import { useLayoutStore } from '@Models/useLayoutModel';

const Quarantine = () => {
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const tableRef = useRef<any>(null);
  const { orgsMap, orgs, orgId, switchOrgId } = useOrgStore();
  const { offset } = useUtcOffsetStore();
  const { bannerHeight, headerHeight } = useLayoutStore();
  const {
    state,
    setQuarantineType,
    setLogsAndFacets,
    setAllFacets,
    setFacets,
    setLogs,
    setTimeRange,
    setTrigger,
    setCriteria,
    setPageStart,
    convertCriteriaToSearchItems,
    setQueryParams,
    setSort,
  } = useQuarantineReducerContext();
  const {
    quarantineType,
    trigger,
    logs,
    facets,
    allFacets,
    timeRange,
    page,
    total,
    criteria,
    sort,
  } = state;
  const [services, setServices] = useState<QuarantineLogTypeEnum[]>([]);
  const [isLoading, toggleLoading] = useToggle(false);
  const [isFacetLoading, toggleFacetLoading] = useToggle(false);

  const {
    showFields,
    mergedFields,
    wrapFields,
    copyFields,
    facets: facetFields,
  } = quarantineConfig[quarantineType];
  // const searchFields = showFields
  //   .reduce((pre, cur) => {
  //     if (quarantineUnsearchableFields.includes(cur)) {
  //       return pre;
  //     }
  //     if (mergedFields[cur]) {
  //       return [...pre, ...mergedFields[cur]];  //no need?
  //     } else {
  //       return [...pre, cur];
  //     }
  //   }, [] as string[])
  //   .map((field) => {
  //     return {
  //       field,
  //       type: timeFields.includes(field) ? 'range' : 'fuzzyMatching',
  //     };
  //   });
  const searchFields = [
    'companyGuid',
    ...showFields.filter(
      (field) => !quarantineUnsearchableFields.includes(field)
    ),
    'all',
  ].map((field) => {
    return {
      field,
      type: timeFields.includes(field as any) ? 'range' : 'fuzzyMatching',
    };
  });
  const constructLogItem = (items: any[]): any[] => {
    const ret: any[] = [];
    items.forEach((item: any) => {
      const retObj: { [k: string]: any } = {};
      //convert some field value to array
      for (const key in item) {
        const value = item[key];
        if (
          logArrayFields.indexOf(key as any) >= 0 &&
          value &&
          !Array.isArray(value)
        ) {
          item[key] = [value];
        }
      }
      if (item['companyGuid']) {
        const orgName = orgsMap.get(item['companyGuid']);
        item['orgName'] = orgName
          ? orgName
          : t('common:common.hints.deleted') + ':' + item['companyGuid'];
      }

      //提前保存一下 原始的fileName，后面做action会用到
      retObj['fileNamePath'] = {
        [item.uniqueId]: item.fileName,
      };

      if (item.filePathInZip) {
        item.fileName = item.fileName + item.filePathInZip;
      }

      if (item.actionSource === 'Phishing') {
        item.actionSource = '';
      }

      //直接把原始值拷贝过来
      copyFields.forEach((copyField) => {
        if (typeof copyField === 'string') {
          retObj[copyField] = item[copyField];
        } else if (copyField.from && copyField.to && item[copyField.from]) {
          retObj[copyField.to] = item[copyField.from];
        }
      });

      showFields.forEach((showField) => {
        let keyField = '';
        //merged field
        if (mergedFields[showField]) {
          mergedFields[showField].forEach(function (mergedField) {
            if (
              item['filterType'] === 'Advanced Spam Protection' &&
              showField === 'mergedSecurityRiskName' &&
              mergedField !== 'spamCategory'
            ) {
              return;
            }

            //这里的mergedField是子的merge字段，下面代码是找到第一个有值的子merge字段，给showField
            if (item[mergedField] && item[mergedField] !== '' && !keyField) {
              keyField = mergedField;
              //copy original key value
              retObj[mergedField] = item[mergedField];
            }
          });
        } else {
          keyField = showField;
        }

        //两种情况：
        //showField不是merge字段的话，keyField = showField;
        //showField是merge字段的话, keyField是个子字段
        if (keyField && item[keyField]) {
          if (keyField === 'timestamp') {
            retObj['timestamp'] = dayjs(item['timestamp'])
              .utcOffset(offset)
              .format(t('common.momentDateFormat'));
          } else if (
            item['filterType'] === 'Data Loss Prevention' &&
            showField === 'mergedSecurityRiskName'
          ) {
            //fix case 7102, DLP log should not show security risk name
            retObj[showField] = '';
          } else if (
            riskAiName.includes(item['filterType']) &&
            showField === 'mergedSecurityRiskName'
          ) {
            retObj[showField] = item['raiMatchedRules']?.[0]?.name;
          } else if (
            wrapFields.indexOf(keyField) >= 0 &&
            wrapLogFieldFuncMap.get(keyField)
          ) {
            retObj[showField] = (
              wrapLogFieldFuncMap.get(keyField) as (row: any) => string
            )(item);
          } else if (mappingFields.indexOf(keyField as MappingField) >= 0) {
            //deal with many i18n
            retObj[showField] = t(
              `quarantine.${keyField}.${item[keyField]}`,
              item[keyField]
            );
          } else {
            retObj[showField] = item[keyField];
          }
        } else {
          retObj[showField] = '';
        }
      });

      ret.push(retObj);
    });
    return ret;
  };

  const fetchData = async () => {
    if (services.length === 0) {
      setLogsAndFacets([], 0, {});
      return;
    }
    // need to update search time range
    let latestTimeRange = state.timeRange;
    if (latestTimeRange.predefined !== LogTimeUnit.Custom) {
      latestTimeRange = getTimeRange(
        latestTimeRange.predefined,
        state.quarantineType
      );
    }
    setTimeRange(latestTimeRange);
    const [contentParams, facetParams] =
      constructorQuarantineContentAndFacetApiParams({
        ...state,
        timeRange: latestTimeRange,
      });
    async function getContentLogs() {
      toggleLoading(true);
      const contentRes =
        await QuarantineApi.apiGetQuarantineLogContent(contentParams);
      setLogs(
        constructLogItem(contentRes.data.contentList as QuarantineLogItem[]), //TODO: 某些属性不属于QuarantineLogItem类型了，例如fileNamePath
        contentRes.data.total
      );
      toggleLoading(false);
    }
    async function getFacetLogs() {
      toggleFacetLoading(true);
      const allFacetRes =
        await QuarantineApi.apiGetQuarantineFacetContent(facetParams);
      setAllFacets(allFacetRes.data);
      toggleFacetLoading(false);
    }
    try {
      if (JSON.parse(contentParams.searches).length > 0) {
        const searches = JSON.parse(contentParams.searches).filter(
          (item: { field: string; values: string[] }) =>
            item.values.length && item.values[0] !== ''
        );
        if (searches.length === 0) {
          return;
        }
        contentParams.searches = JSON.stringify(searches);
        facetParams.searches = JSON.stringify(searches);
      }
      getContentLogs().then((r) => r);
      getFacetLogs().then((r) => r);
    } catch (error) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
      toggleLoading(false);
      toggleFacetLoading(false);
    } finally {
      if (tableRef.current) {
        tableRef.current.firstChild.scrollTop = 0;
      }
    }
  };

  const appendData = async () => {
    const scrollTop = tableRef.current.firstChild.scrollTop;
    try {
      const [contentParams, facetParams] =
        constructorQuarantineContentAndFacetApiParams(state);
      if (JSON.parse(contentParams.searches).length > 0) {
        const searches = JSON.parse(contentParams.searches).filter(
          (item: { field: string; values: string[] }) =>
            item.values.length && item.values[0] !== ''
        );
        if (searches.length === 0) {
          return;
        }
        contentParams.searches = JSON.stringify(searches);
        facetParams.searches = JSON.stringify(searches);
      }
      toggleLoading(true);
      const contentData = (
        await QuarantineApi.apiGetQuarantineLogContent(contentParams)
      ).data;
      //处理contentData
      const allFacetData = (
        await QuarantineApi.apiGetQuarantineFacetContent(facetParams)
      ).data;
      setLogsAndFacets(
        [
          ...logs,
          ...constructLogItem(contentData.contentList as QuarantineLogItem[]),
        ],
        contentData.total,
        allFacetData
      );
    } catch (error) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      toggleLoading(false);
      tableRef.current.firstChild.scrollTop = scrollTop;
    }
  };

  useEffect(() => {
    if (page.start === 1) {
      return;
    }
    appendData();
  }, [page.start]);

  useEffect(() => {
    if (!trigger) {
      return;
    }
    fetchData();
  }, [trigger]);

  useEffect(() => {
    (async () => {
      const serviceIds = (await ProvisionApi.getProvisionedServices(orgId))
        .data;
      const excludeServices = [
        SERVICE_NAME_ID_MAP[ServiceNameEnum.TeamsChat],
        SERVICE_NAME_ID_MAP[ServiceNameEnum.MicrosoftIdentityProtection],
        SERVICE_NAME_ID_MAP[ServiceNameEnum.MicrosoftInformationProtection],
      ];
      const sortedServiceNames = getSortedServiceIds(serviceIds)
        .filter((x) => !excludeServices.includes(x))
        .map(
          (id) => SERVICE_ID_NAME_MAP[id]
        ) as unknown as QuarantineLogTypeEnum[]; //maybe empty arr

      setServices(sortedServiceNames);
      const searchParams = new URL(window.location.toString()).searchParams;
      if (isInUIC()) {
        removeUrlState();
      }
      const type = convertScanSourceToQuarantineType(
        searchParams.get('scanSource')
      );
      if (!type) {
        setQuarantineType(
          sortedServiceNames[0]
            ? sortedServiceNames[0]
            : QuarantineLogTypeEnum.Exchange
        );
      } else {
        const _facets = JSON.parse(
          decodeURIComponent(searchParams.get('facets') || '[]')
        ) as SearchItem[];
        const _timeRange = JSON.parse(
          decodeURIComponent(
            searchParams.get('timeRange') || '{"predefined": "24h"}'
          )
        ) as TimeRangeInterface;

        setQueryParams(type, _facets, _timeRange);
      }

      const _criteria = (
        JSON.parse(
          decodeURIComponent(searchParams.get('criteria') || '[]')
        ) as Criterion[]
      ).map((item) => ({
        ...item,
        label: t(`quarantine.${item.field}`),
      }));
      if (_criteria.length > 0) {
        setCriteria(_criteria);
        const _criteriaTimeRange = JSON.parse(
          decodeURIComponent(
            searchParams.get('timeRange') || '{"predefined": "1M"}'
          )
        ) as TimeRangeInterface;
        setTimeRange(_criteriaTimeRange);
      }
    })();
  }, [orgId]);

  useEffect(() => {
    const searchParams = new URL(window.location.toString()).searchParams;
    if (isInUIC()) {
      removeUrlState();
    }
    const companyGuid = searchParams.get('companyGuid');
    //如果不传companyGuid，就当作不是跳转
    if (!companyGuid) {
      return;
    }

    if (orgsMap.has(companyGuid)) {
      switchOrgId(companyGuid);
    } else {
      switchOrgId('all');
    }
    //处理url中的参数，看来只有一个tm_violator,塞进facet
    //https://admin-dev.tmcas.trendmicro.net/#!/quarantine/box-quarantine?filter_enabled={"tm_violator":{"yuki_wang@trendmicro.com.cn":true}}
    //还有个这个， 这个是单独的一个log的跳转，传的是tm_unique_id
    //https://admin-dev.tmcas.trendmicro.net/#!/quarantine/mail-quarantine?filter_enabled={"tm_unique_id":{"AAkALgAAAAAAHYQDEapmEc2byACqAC-EWg0AVmfL1qMBpUeXGN0ZSMJfBQABA0ixyAAA":true}}&pre_filter={"path":"data_protection","time_range":null,"filter_enabled":{},"vs_facts":[]}
  }, []);

  const constructorQuarantineContentAndFacetApiParams = (
    quarantineState: QuarantineLogState
  ): [QuarantineLogContentReqDto, QuarantineLogFacetReqDto] => {
    const { quarantineType, page, sort, timeRange, facets } = quarantineState;
    const serviceName = QUARANTINE_TYPE_NAME_MAP.get(quarantineType) as string;
    const contentListParams: QuarantineLogContentReqDto = {
      serviceName,
      page,
      sort,
      timeRange,
      searches: JSON.stringify([
        ...facets,
        ...convertCriteriaToSearchItems(quarantineState),
      ]),
    };
    const facetsParams: QuarantineLogFacetReqDto = {
      serviceName,
      timeRange,
      searches: JSON.stringify([
        ...facets,
        ...convertCriteriaToSearchItems(quarantineState),
      ]),
    };
    return [contentListParams, facetsParams];
  };
  if (!trigger) {
    return <></>;
  }

  return (
    <Flex
      maxWidth="100%"
      height={`calc(100vh - ${headerHeight}px - 54px - ${bannerHeight}px)`}
      overflow="auto"
      direction="column"
      backgroundColor={colorStyle.background.primary}
    >
      <Flex gap="12px" px="4x" py="3x" align="center">
        <OrgMenu
          menuItems={orgs || []}
          selectedKey={orgId}
          selectedValue={orgsMap.get(orgId) as string}
          onClick={(e: any) => {
            const orgId = e.target.getAttribute('value');
            switchOrgId(orgId);
          }}
        />
        {services.length > 0 && (
          <ServiceMenu
            menuItems={services.map((s) => {
              return {
                key: s,
                value: t(`common.service.${s}`),
              };
            })}
            selectedKey={quarantineType}
            selectedValue={t(`common.service.${quarantineType}`)}
            onClick={(e: any) => {
              const service = e.target.getAttribute('value');
              setQuarantineType(service);
            }}
          />
        )}
      </Flex>
      <Flex
        px="4x"
        py="3x"
        backgroundColor={
          colorMode === 'dark' ? colorStyle.background.secondary : '#FFFFFF'
        }
        align="center"
      >
        <SearchCriteria
          logType={quarantineType}
          criteria={criteria}
          setCriteria={setCriteria}
          searchFields={searchFields}
        />
      </Flex>
      <Flex
        justifyContent="space-between"
        align="center"
        backgroundColor={
          colorMode === 'dark'
            ? colorStyle.background.secondary
            : colorStyle.background.primary
        }
        borderTopColor={colorStyle.background.tertiary}
        borderTopStyle="solid"
        borderTopWidth="1px"
        px="4x"
        py="3x"
      >
        <Text
          color={colorStyle.color.secondary}
          dangerouslySetInnerHTML={{
            __html: t('log.hint.showItems', {
              TOTAL: total,
              CURRENT:
                page.start * page.size > total ? total : page.start * page.size,
            }),
          }}
        />
        <Flex align="center" gap="8px">
          <TimeRange
            type={quarantineType}
            timeRange={timeRange}
            setTimeRange={setTimeRange}
          />
          <Divider variant="solid" orientation="vertical" height="30px" />
          {/* TODO: add pendo */}
          <Button
            variant="primary"
            onClick={setTrigger}
            disabled={services.length === 0}
          >
            <Icon as={SearchOIcon} />
            &nbsp;{t('log.button.search')}
          </Button>
        </Flex>
      </Flex>
      <Flex
        flex={1}
        borderTop="1px solid"
        borderColor={colorMode === 'dark' ? 'gray:60' : 'gray:30'}
      >
        <QuarantineFacet
          allFacets={allFacets}
          facets={facets}
          setFacets={setFacets}
          facetsOrder={facetFields}
          isLoading={isFacetLoading}
        />
        {services.length > 0 && (
          <QuarantineTable
            logs={logs}
            type={quarantineType}
            key={`${orgId}-${quarantineType}-${trigger?.toISOString()}`}
            ref={tableRef}
            total={total}
            page={page}
            sort={sort}
            setPageStart={setPageStart}
            setTrigger={setTrigger}
            setSort={setSort}
            isLoading={isLoading}
          />
        )}
      </Flex>
    </Flex>
  );
};

export default function QuarantineLogWithStore() {
  return (
    <QuarantineLogContextProvider>
      <Quarantine />
    </QuarantineLogContextProvider>
  );
}

interface MenuProps {
  menuItems: MenuOption[];
  selectedKey: string;
  selectedValue: string;
  onClick: (e: any) => void;
}

const OrgMenu = ({
  menuItems,
  selectedKey,
  selectedValue,
  onClick,
}: MenuProps) => {
  const { t } = useTranslation();

  return (
    <Menu>
      <MenuButton variant="secondary" width="288px">
        <Flex maxWidth="240px">
          <TextLabel mr="2x">{t('common.label.organization')}:</TextLabel>
          <OverflowTooltip
            PopperProps={{ usePortal: true }}
            label={selectedValue}
          >
            {selectedValue}
          </OverflowTooltip>
        </Flex>
      </MenuButton>
      <MenuList width="288px" maxHeight="400px" overflow="auto">
        {menuItems.map((item) => (
          <MenuItem onClick={onClick} key={item.key} value={item.key}>
            {item.value}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

const ServiceMenu = ({
  menuItems,
  selectedKey,
  selectedValue,
  onClick,
}: MenuProps) => {
  const { t } = useTranslation();

  return (
    <Menu>
      <MenuButton variant="secondary" width="288px">
        <Flex maxWidth="240px">
          <TextLabel mr="2x">{t('common.label.service')}:</TextLabel>
          <OverflowTooltip
            PopperProps={{ usePortal: true }}
            label={selectedValue}
          >
            {selectedValue}
          </OverflowTooltip>
        </Flex>
      </MenuButton>
      <MenuList width="288px" maxHeight="400px" overflow="auto">
        {menuItems.map((item) => (
          <MenuItem onClick={onClick} key={item.key} value={item.key}>
            {item.value}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};
